'use client';

import { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Container,
  Paper,
  Grid,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  CheckCircle,
  WarningAmber,
  ArrowForward,
  ExpandMore,
  VerifiedUser,
  AutoFixHigh,
  ReportProblem,
  Groups,
  Troubleshoot,
  ShowChart,
  TrackChanges,
  Autorenew,
  FactCheck,
  TaskAlt,
  HealthAndSafety,
  ContentCopy,
  Bolt,
  Assessment,
} from '@mui/icons-material';
import Header from '../../components/HomepageContent/Header';
import FeatureHeroCtaStack from '../components/FeatureHeroCtaStack';
import FeatureResourceButtons from '../components/FeatureResourceButtons';
import Footer from '../../components/HomepageContent/Footer';
import Link from 'next/link';
import BrandName from '../../components/BrandName';
import WorkflowStepper from '../../components/WorkflowStepper';
import '../features-responsive.css';

const BRAND_BLUE = '#0F4C9C';
const BRAND_GREEN = '#63A821';

const workflowSteps = [
  { title: 'Define the Project', description: 'Problem statement, areas, priority, and timeline' },
  { title: 'Build Team & Scope', description: 'Assign members, roles, and scope departments' },
  { title: 'Find Root Causes', description: 'Run documented RCA with 5 Whys or fishbone tools' },
  { title: 'Set SMART Goals', description: 'Define metrics with targets, and record data over time' },
  { title: 'Run PDSA Cycles', description: 'Plan, Do, Study, Act—with interventions and pre/post values' },
  { title: 'Review & Conclude', description: 'Committee review, sustainability plan, and formal approval' }
];

const painPoints = [
  'PIPs defined as good intentions instead of problem statements, baselines, and targets',
  'Root cause analysis done verbally—no documented 5 Whys or fishbone to stand behind',
  'Goals that aren\'t specific or measurable, so success can\'t be demonstrated',
  'PDSA cycles that happen informally with no record of what was tested or learned',
  'No data trail connecting interventions to measured improvement',
  'Committee review that\'s hard to evidence when surveyors ask about your QAPI program',
];

const capabilities = [
  {
    icon: AutoFixHigh,
    title: 'Guided PIP Builder',
    description: 'An eight-step wizard structures every project: project details, team and scope, root cause analysis, metrics and measures, SMART goals, interventions, review, and conclusion—so nothing gets skipped and every PIP follows the same defensible method.',
  },
  {
    icon: ReportProblem,
    title: 'Structured Problem Definition',
    description: 'Capture the project name, problem statement, problem areas, rationale, and initiation source, plus priority, start date, target end date, evaluation frequency, and final review date. Document baseline data and expected outcomes up front.',
  },
  {
    icon: Groups,
    title: 'Team, Roles & Scope',
    description: 'Assign team members with defined roles and responsibilities, designate a primary lead, and scope the project to specific departments and units—so accountability is explicit from day one.',
  },
  {
    icon: Troubleshoot,
    title: 'Root Cause Analysis Tools',
    description: 'Run documented RCA using 5 Whys or fishbone methodology—with AI-assisted analysis—and record findings with descriptions, categories, evidence, and priority, including which improvement cycle addresses each finding.',
  },
  {
    icon: ShowChart,
    title: 'Measures, Targets & Trend Data',
    description: 'Define each measure with type, numerator and denominator, target value, benchmark, frequency, and unit. Record data points over time with dates, values, and sources—so improvement is shown with data, not anecdotes.',
  },
  {
    icon: TrackChanges,
    title: 'SMART Goals',
    description: 'Document specific, measurable goals per project so the committee—and a surveyor—can see exactly what success was defined as and whether it was achieved.',
  },
  {
    icon: Autorenew,
    title: 'PDSA Cycles & Interventions',
    description: 'Run numbered Plan-Do-Study-Act cycles with descriptions for each phase, start and end dates, status, pre- and post-cycle values, change percentage, and key findings. Attach interventions to each cycle with responsible roles, dates, effectiveness notes, and cost estimates.',
  },
  {
    icon: FactCheck,
    title: 'Committee QAPI Review',
    description: 'PIPs surface in committee meetings in a Ready for Review queue. Reviewers record comments and flag concerns in progress—creating a documented oversight trail tied to the meeting where the review happened.',
  },
  {
    icon: TaskAlt,
    title: 'Formal Conclusion & Approval',
    description: 'Close every PIP with a goal-met determination, sustainability and maintenance plan, a named sustainability monitor, and formal approval—so the project ends with evidence, not a fade-out.',
  },
  {
    icon: HealthAndSafety,
    title: 'Health Equity Focus',
    description: 'Flag projects as health-equity focused and document equity analysis alongside baseline data and expected outcomes.',
  },
  {
    icon: ContentCopy,
    title: 'PIP Templates',
    description: 'Start new projects from reusable PIP templates so recurring improvement themes launch with consistent structure instead of a blank page.',
  },
  {
    icon: Bolt,
    title: 'KPI-Triggered PIPs',
    description: 'Configure audit and pathway KPI thresholds to trigger new PIPs—with a name, due window, and assignee—so quality data failures turn into improvement projects instead of dashboard red flags.',
  },
  {
    icon: Assessment,
    title: 'PIP Report Generation',
    description: 'Generate a PIP report directly from the conclusion step—a clean, shareable record of the project from problem statement through outcome. PIPs also appear in QAPI and committee meeting reports.',
  },
];

const roles = [
  {
    title: 'QAPI & quality leaders',
    benefit: 'A consistent, evidence-based method for every improvement project',
  },
  {
    title: 'Administrators & DONs',
    benefit: 'Visibility into active PIPs, outcomes, and committee review status',
  },
  {
    title: 'PIP team leads',
    benefit: 'One workspace for RCA, goals, data points, and PDSA cycles—no scattered documents',
  },
  {
    title: 'Committee members',
    benefit: 'Ready-for-review queues with structured review comments inside the meeting',
  },
  {
    title: 'Corporate / multi-site operators',
    benefit: 'Templates and a uniform PIP methodology across every facility',
  },
];

const outcomes = [
  { value: 'Standard', label: 'Documented methodology', detail: 'Every PIP follows the same RCA → goals → PDSA → conclusion structure' },
  { value: 'Proven', label: 'Data-backed improvement', detail: 'Measures with targets, benchmarks, and trend data points' },
  { value: 'Clear', label: 'Cycle-level learning', detail: 'Pre/post values, change percentage, and key findings per PDSA cycle' },
  { value: 'Tracked', label: 'Committee oversight', detail: 'Review comments and concern flags recorded against meetings' },
  { value: 'Closed', label: 'Defensible closure', detail: 'Goal-met determination, sustainability plan, and formal approval' },
  { value: '100%', label: 'Program consistency', detail: 'Templates and a uniform wizard across facilities' },
];

const integrations = [
  'Committee Meetings',
  'QAPI Reports',
  'Audit & Pathway KPIs',
  'Templates',
  'Form Wizard',
];

const complianceItems = [
  'Documented root cause analysis using recognized tools (5 Whys, fishbone)',
  'Measurable goals with numerators, denominators, targets, and benchmarks',
  'PDSA cycle records showing what was tested, what changed, and what was learned',
  'Committee review trail tied to specific meetings',
  'Formal conclusion with goal-met determination, sustainability monitoring, and approval',
  'A complete project history from problem statement to outcome',
];

const faqs = [
  {
    q: 'Does CareAscendium support PDSA cycles?',
    a: 'Yes. Each PIP can run numbered Plan-Do-Study-Act cycles with phase descriptions, dates, status, pre- and post-cycle values, change percentage, and key findings—with interventions attached to each cycle.',
  },
  {
    q: 'How is root cause analysis handled?',
    a: 'RCA is a dedicated step in the PIP wizard with 5 Whys and fishbone tools, AI-assisted analysis, and structured findings—each with a description, category, evidence, priority, and the cycle that addresses it.',
  },
  {
    q: 'How do PIPs connect to our QAPI committee?',
    a: 'PIPs appear in committee meetings in a Ready for Review queue. Reviewers document comments and flag in-progress concerns, and the review history is tied to the meeting record. PIPs also appear in QAPI report output.',
  },
  {
    q: 'Can we measure whether a PIP actually worked?',
    a: 'Yes. Define measures with targets and benchmarks, record data points over time, and capture pre/post values per PDSA cycle. The conclusion step documents whether the goal was met and how gains will be sustained.',
  },
  {
    q: 'Can PIPs be created automatically?',
    a: 'You can configure audit and pathway KPI thresholds to trigger a new PIP—with a defined name, due window, and assignee—when performance misses target.',
  },
  {
    q: 'Can we standardize PIPs across facilities?',
    a: 'Yes. PIP templates let multi-site operators launch projects with consistent structure, and the guided wizard enforces the same methodology everywhere.',
  },
  {
    q: 'Is there mobile access?',
    a: 'The CareAscendium mobile app supports viewing PIPs and basic creation with SMART goals. The full eight-step wizard—including RCA, measures, PDSA cycles, and conclusion—is designed for the web.',
  },
];

export default function PerformanceImprovementPlansClient() {
  const [expanded, setExpanded] = useState<number | false>(0);

  const handleChange = (panel: number) => (event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded ? panel : false);
  };

  useEffect(() => {
    document.title = 'Performance Improvement Plan (PIP) Software for Skilled Nursing Facilities | CareAscendium';
  }, []);

  return (
    <Box className="feature-page-wrapper" sx={{ minHeight: '100vh', bgcolor: '#fafafa', display: 'flex', flexDirection: 'column' }}>
      <Header />

      {/* Hero */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${BRAND_BLUE} 0%, #1E4A8C 50%, #2A5A9C 100%)`,
          py: { xs: 6, md: 8 },
          px: 4,
          textAlign: 'center',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Chip
            label="Performance Improvement Plans"
            sx={{
              bgcolor: 'rgba(255,255,255,0.15)',
              color: 'white',
              fontWeight: 600,
              mb: 3,
              border: '1px solid rgba(255,255,255,0.3)',
            }}
          />
          <Typography
            variant="h1"
            sx={{
              color: 'white',
              mb: 3,
              fontWeight: 800,
              fontSize: { xs: '2rem', md: '3.25rem' },
              lineHeight: 1.15,
              mx: 'auto',
              maxWidth: 1000,
            }}
          >
            Run Real Performance Improvement Projects—RCA, SMART Goals, PDSA Cycles, and Committee Review in One Guided Workflow
          </Typography>
          <Typography
            variant="h5"
            sx={{
              color: 'rgba(255,255,255,0.92)',
              mx: 'auto',
              lineHeight: 1.6,
              fontWeight: 300,
              fontSize: { xs: '1.05rem', md: '1.35rem' },
              mb: 5,
              maxWidth: 900,
            }}
          >
            <BrandName variant="dark" style={{ fontSize: 'inherit' }} /> gives skilled nursing facilities a structured PIP workspace built for QAPI—from problem statement and root cause analysis through measurable goals, PDSA improvement cycles, committee review, and a formally approved conclusion.
          </Typography>
          <FeatureHeroCtaStack>
            <Link href="/contact" style={{ textDecoration: 'none' }}>
              <Button
                variant="contained"
                size="large"
                endIcon={<ArrowForward />}
                sx={{
                  bgcolor: BRAND_GREEN,
                  color: 'white',
                  px: 4,
                  py: 1.5,
                  fontWeight: 700,
                  borderRadius: '6px',
                  textTransform: 'none',
                  '&:hover': { bgcolor: '#55901d' },
                }}
              >
                REQUEST A DEMO
              </Button>
            </Link>
            <FeatureResourceButtons />
          </FeatureHeroCtaStack>
        </Container>
      </Box>

      {/* Problem */}
      <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 } }}>
        <Grid container spacing={5} alignItems="center" justifyContent="center">
          <Grid size={{ xs: 12, md: 10 }} sx={{ textAlign: 'center' }}>
            <Typography variant="h2" sx={{ color: BRAND_BLUE, mb: 3, fontWeight: 700, fontSize: { xs: '1.75rem', md: '2.25rem' } }}>
              The Challenge with PIPs That Live in PowerPoint
            </Typography>
            <Typography sx={{ color: '#666', mb: 4, lineHeight: 1.7, fontSize: '1.05rem', maxWidth: 850, mx: 'auto' }}>
              Every QAPI program is expected to run performance improvement projects—but in most SNFs, a PIP is a slide deck and a vague goal. The root cause analysis is informal, the baseline data is missing, PDSA cycles are never documented, and when the committee asks "did it work?", nobody can prove it either way.
            </Typography>
            <Box component="ul" sx={{ pl: 0, listStyle: 'none', m: 0, display: 'inline-block', textAlign: 'left' }}>
              {painPoints.map((point) => (
                <Box
                  component="li"
                  key={point}
                  sx={{ display: 'flex', alignItems: 'flex-start', mb: 1.5, gap: 1.5 }}
                >
                  <WarningAmber sx={{ color: '#FF9800', mt: 0.2, flexShrink: 0 }} />
                  <Typography sx={{ color: '#555', lineHeight: 1.5 }}>{point}</Typography>
                </Box>
              ))}
            </Box>
          </Grid>
          <Grid size={{ xs: 12, md: 10 }}>
            <Paper
              elevation={4}
              sx={{
                p: 4,
                borderRadius: 3,
                background: 'linear-gradient(145deg, #F3F1FF 0%, #ffffff 100%)',
                borderLeft: `4px solid ${BRAND_BLUE}`,
              }}
            >
              <Typography
                sx={{
                  fontStyle: 'italic',
                  color: BRAND_BLUE,
                  fontSize: '1.15rem',
                  lineHeight: 1.6,
                  fontWeight: 500,
                }}
              >
                &ldquo;A performance improvement project without documented root causes, measures, and cycles isn't a project—it's a hope.&rdquo;
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </Container>

      {/* Solution / Workflow */}
      <Box sx={{ bgcolor: '#f0f4fa', py: { xs: 4, md: 6 } }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography variant="h2" sx={{ color: BRAND_BLUE, mb: 2, fontWeight: 700 }}>
              One Guided Workflow. From Problem Statement to Proven Outcome.
            </Typography>
            <Typography sx={{ color: '#666', maxWidth: 780, mx: 'auto', lineHeight: 1.7, fontSize: '1.05rem' }}>
              <BrandName variant="light" style={{ fontSize: 'inherit' }} /> walks your team through a complete, structured PIP—project definition, team and scope, root cause analysis, metrics, SMART goals, PDSA cycles with interventions, and a formal conclusion—so every improvement project is documented, measured, and defensible.
            </Typography>
          </Box>
          <WorkflowStepper steps={workflowSteps} />
        </Container>
      </Box>

      {/* Capabilities */}
      <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 } }}>
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography variant="h2" sx={{ color: BRAND_BLUE, mb: 2, fontWeight: 700 }}>
            Key Capabilities
          </Typography>
        </Box>
        <Box 
          sx={{ 
            display: 'grid', 
            gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'repeat(2, minmax(0, 1fr))' }, 
            gap: 3,
            alignItems: 'stretch'
          }}
        >
          {capabilities.map((cap) => (
            <Paper
              key={cap.title}
              elevation={3}
              sx={{
                p: 3,
                height: '100%',
                borderRadius: 3,
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 12px 28px rgba(21,60,127,0.12)',
                },
              }}
            >
              <Box
                sx={{
                  width: 52,
                  height: 52,
                  borderRadius: '50%',
                  bgcolor: `${BRAND_BLUE}15`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: 2,
                }}
              >
                <cap.icon sx={{ color: BRAND_BLUE, fontSize: 26 }} />
              </Box>
              <Typography variant="h6" sx={{ color: BRAND_BLUE, fontWeight: 700, mb: 1.5 }}>
                {cap.title}
              </Typography>
              <Typography sx={{ color: '#666', mb: 2, lineHeight: 1.6, fontSize: '0.95rem' }}>
                {cap.description}
              </Typography>
            </Paper>
          ))}
        </Box>
      </Container>

      {/* Roles + Integration */}
      <Box sx={{ bgcolor: '#f0f4fa', py: { xs: 4, md: 6 } }}>
        <Container maxWidth="lg">
          <Grid container spacing={5} justifyContent="center" alignItems="stretch">
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="h2" sx={{ color: BRAND_BLUE, mb: 3, fontWeight: 700, fontSize: '1.75rem' }}>
                Built for Every Role
              </Typography>
              {roles.map((role) => (
                <Box key={role.title} sx={{ display: 'flex', gap: 1.5, mb: 2.5 }}>
                  <CheckCircle sx={{ color: BRAND_GREEN, mt: 0.2, flexShrink: 0 }} />
                  <Box>
                    <Typography sx={{ fontWeight: 700, color: BRAND_BLUE }}>{role.title}</Typography>
                    <Typography sx={{ color: '#666', fontSize: '0.95rem', lineHeight: 1.5 }}>
                      {role.benefit}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Paper
                elevation={3}
                sx={{
                  p: { xs: 3, md: 4 },
                  borderRadius: 3,
                  height: '100%',
                  width: '100%',
                  background: `linear-gradient(145deg, ${BRAND_BLUE} 0%, #1E4A8C 100%)`,
                  color: 'white',
                  textAlign: 'center',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <Typography variant="h3" sx={{ fontWeight: 700, mb: 2, fontSize: '1.5rem' }}>
                  Part of the <BrandName variant="dark" style={{ fontSize: 'inherit' }} /> Platform
                </Typography>
                <Typography sx={{ color: '#FFFFFF', fontWeight: 500, mb: 3, lineHeight: 1.6 }}>
                  Performance Improvement Plans connect directly to the quality infrastructure around them:
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 3, justifyContent: 'center' }}>
                  {integrations.map((item) => (
                    <Chip
                      key={item}
                      label={item}
                      sx={{
                        bgcolor: 'rgba(255,255,255,0.15)',
                        color: 'white',
                        fontWeight: 600,
                        border: '1px solid rgba(255,255,255,0.25)',
                      }}
                    />
                  ))}
                </Box>
                <Typography sx={{ fontWeight: 600, color: '#a8d86a', fontStyle: 'italic' }}>
                  One platform. One source of truth. Improvement you can prove.
                </Typography>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Outcomes & Compliance */}
      <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6 } }}>
        <Box sx={{ textAlign: 'center', mb: 5 }}>
          <Typography variant="h2" sx={{ color: BRAND_BLUE, mb: 2, fontWeight: 700 }}>
            Outcomes You Can Measure
          </Typography>
        </Box>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
            gap: 3,
            mb: 8,
            alignItems: 'stretch',
            maxWidth: 1000,
            mx: 'auto'
          }}
        >
          {outcomes.map((item) => (
            <Paper
              key={item.label}
              elevation={2}
              sx={{
                p: 3,
                textAlign: 'center',
                borderRadius: 3,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
              }}
            >
              <Typography sx={{ fontSize: '1.5rem', fontWeight: 700, color: BRAND_GREEN, mb: 0.5 }}>
                {item.value}
              </Typography>
              <Typography sx={{ fontWeight: 700, color: BRAND_BLUE, mb: 0.5, fontSize: '0.95rem' }}>
                {item.label}
              </Typography>
              <Typography sx={{ color: '#888', fontSize: '0.85rem' }}>{item.detail}</Typography>
            </Paper>
          ))}
        </Box>

        <Box sx={{ textAlign: 'center', mb: 5 }}>
          <Typography variant="h2" sx={{ color: BRAND_BLUE, mb: 2, fontWeight: 700 }}>
            Documentation You Can Stand Behind
          </Typography>
          <Typography sx={{ color: '#666', maxWidth: 780, mx: 'auto', lineHeight: 1.7, fontSize: '1.05rem', mb: 4 }}>
            <BrandName variant="light" style={{ fontSize: 'inherit' }} /> supports the rigor CMS expects from a QAPI performance improvement program:
          </Typography>
          <Box sx={{ textAlign: 'left', width: 'fit-content', mx: 'auto' }}>
            {complianceItems.map((item) => (
              <Box key={item} sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
                <CheckCircle sx={{ color: BRAND_GREEN, mt: 0.5, flexShrink: 0 }} />
                <Typography sx={{ color: '#555', fontSize: '1.05rem', lineHeight: 1.5 }}>
                  {item}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Container>

      {/* FAQ */}
      <Box sx={{ py: { xs: 10, md: 14 }, px: 4, background: 'linear-gradient(113.99deg, #153C7F 12.55%, #000820 87.43%)' }}>
        <Container maxWidth="md">
          <Typography variant="h3" sx={{ fontWeight: 800, color: 'white', mb: 6, textAlign: 'center', fontSize: { xs: '1.75rem', md: '2.5rem' } }}>
            Frequently Asked Questions
          </Typography>
          <Box>
            {faqs.map((faq, i) => (
              <Accordion 
                key={i}
                expanded={expanded === i} 
                onChange={handleChange(i)}
                sx={{ 
                  mb: 2.5, 
                  boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: '12px !important',
                  bgcolor: 'rgba(255, 255, 255, 0.05)',
                  backdropFilter: 'blur(10px)',
                  color: 'white',
                  overflow: 'hidden',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.08)'
                  },
                  '&:before': { display: 'none' }
                }}
              >
                <AccordionSummary expandIcon={<ExpandMore sx={{ color: 'white' }} />} sx={{ px: { xs: 2, md: 4 }, py: 1.5 }}>
                  <Typography sx={{ fontWeight: 700, fontSize: '1.1rem', letterSpacing: '0.02em' }}>{faq.q}</Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ px: { xs: 2, md: 4 }, pb: 4 }}>
                  <Typography sx={{ color: 'rgba(255,255,255,0.85)', lineHeight: 1.8, fontSize: '1.05rem' }}>
                    {faq.a}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        </Container>
      </Box>

      {/* CTA */}
      <Box sx={{ py: 6, bgcolor: '#f0f4fa' }}>
        <Container maxWidth="lg">
          <Box
            sx={{
              backgroundImage: `url('/assets/osha/bgdemo.svg'), linear-gradient(113.99deg, #0F4C9C 12.55%, #000820 87.43%)`,
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
              backgroundSize: 'cover',
              borderRadius: '16px',
              minHeight: '300px',
              py: 5,
              px: 3,
              textAlign: 'center',
              color: 'white',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 15px 40px rgba(0, 8, 32, 0.12)',
            }}
          >
            <Typography variant="h2" sx={{ mb: 2, fontWeight: 800, fontSize: { xs: '1.75rem', md: '2.5rem' }, textTransform: 'uppercase' }}>
              Ready to Run PIPs You Can Prove?
            </Typography>
            <Typography sx={{ mb: 4, opacity: 0.9, lineHeight: 1.6, fontSize: '1.1rem', maxWidth: 850 }}>
              See how <BrandName variant="dark" style={{ fontSize: 'inherit' }} /> turns performance improvement from slide decks into documented, measured, committee-reviewed projects that stand up in survey.
            </Typography>
            <Link href="/contact" style={{ textDecoration: 'none' }}>
              <Button
                variant="contained"
                size="large"
                sx={{
                  bgcolor: BRAND_GREEN,
                  color: 'white',
                  px: 5,
                  py: 1.5,
                  fontWeight: 700,
                  borderRadius: '30px',
                  textTransform: 'none',
                  fontSize: '1.1rem',
                  '&:hover': { bgcolor: '#528a1b' },
                  boxShadow: '0 4px 10px rgba(99, 168, 33, 0.2)',
                }}
              >
                REQUEST A DEMO
              </Button>
            </Link>
          </Box>
        </Container>
      </Box>

      <Footer />
    </Box>
  );
}
